
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import { MdSearch, MdEditSquare } from "react-icons/md";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import ConfirmationModal from "../../../../common/confirm/ConfirmationModal";
import { fetchFilesByLeadId } from "../../../../../store/slices/fileSlice";
import { useDispatch, useSelector } from "react-redux";
import { deleteLead } from './../../../../../store/slices/leadsSlice';
import { setFilteredReports } from "../../../../../store/slices/filteredDataSlice";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FileUploadFollowupTable from "./FileUploadFollowupTable";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  readFollowupTableExpanded,
  writeFollowupTableExpanded,
} from "../../../../../utils/followupTablePrefs";
import { getFeatureAccessLevel } from "../../../../../utils/featureAccessUtils";

const FollowupTableL1 = ({ wrapperClasses }) => {
  const dispatch = useDispatch()
  const [, setFilteredLeadReports] = useState([]);
  const [LeadReports, setLeadReports] = useState([]);
  const [tableLeadReports, setTableLeadReports] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [attchmentLead, setattchmentLead] = useState("");

  const { filteredReports } = useSelector((state) => state.filteredData);
  const groupsFromSlice = useSelector((state) => state.group?.group || []);
  const groupNameById = useMemo(() => {
    const normalize = (value) => String(value ?? "").trim();
    const map = new Map();
    const groups = Array.isArray(groupsFromSlice) ? groupsFromSlice : [];
    groups.forEach((groupItem) => {
      const groupId = normalize(groupItem?._id ?? groupItem?.id);
      if (!groupId) return;
      const groupName = normalize(
        groupItem?.name ??
          groupItem?.newGroupName ??
          groupItem?.groupName ??
          groupItem?.GroupName ??
          groupId
      );
      map.set(groupId, groupName || groupId);
    });
    return map;
  }, [groupsFromSlice]);
  const getLeadGroupName = useCallback(
    (lead) => {
      const normalize = (value) => String(value ?? "").trim();
      const groupIdFromObject = normalize(lead?.groupid?._id ?? lead?.groupid?.id);
      if (groupIdFromObject && groupNameById.has(groupIdFromObject)) {
        return groupNameById.get(groupIdFromObject);
      }
      const groupIdFromString = typeof lead?.groupid === "string" ? normalize(lead.groupid) : "";
      if (groupIdFromString && groupNameById.has(groupIdFromString)) {
        return groupNameById.get(groupIdFromString);
      }
      return (
        lead?.AssignedGroupName ||
        lead?.AssignedGroup ||
        lead?.groupid?.name ||
        groupIdFromString ||
        ""
      );
    },
    [groupNameById]
  );
  const { session } = useSelector((state) => state.session);
  const { employees } = useSelector((state) => state.employees);
  const userAuthLevel = session?.auth || localStorage.getItem("auth") || "";

  const email = localStorage.getItem("email");
  const currentEmployee = employees?.find((emp) => emp.EmpEmail === email);
  const hasNoFeatureConfig =
    Array.isArray(currentEmployee?.featureAccess) && currentEmployee.featureAccess.length === 0;

  const followUpAccessLevel = getFeatureAccessLevel(currentEmployee, "FOLLOW-UP-TABLE");
  const isViewOnly = !!currentEmployee && !hasNoFeatureConfig &&
    followUpAccessLevel &&
    String(followUpAccessLevel).trim().toUpperCase() === "VIEW ONLY";

  useEffect(() => {
    dispatch(setFilteredReports())
  }, [dispatch]);

  // useEffect(() => {
  //   if (filteredReports) {
  //     setFilteredLeadReports(filteredReports);
  //     setLeadReports(filteredReports)
  //   }
  // }, [filteredReports, dispatch]);

  const parseDate = useCallback((dateStr) => {
    if (!dateStr) return null;

    // First try: Direct Date parsing
    let parsedDate = new Date(dateStr);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }

    // Second try: MM-DD-YYYY format (most common in your data)
    const parts = dateStr.toString().split('-');
    if (parts.length === 3) {
      const [month, day, year] = parts.map(Number);
      // Validate date components
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year > 2000) {
        const newDate = new Date(year, month - 1, day);
        if (!isNaN(newDate.getTime())) {
          return newDate;
        }
      }
    }

    return null;
  }, []);
  // FIXED: getColorPriority function - returns priority number (1=high, 2=medium, 3=low)
  const getColorPriority = useCallback((date) => {
    if (!date) return 3; // No date = low priority

    const today = new Date();
    const followDate = parseDate(date);

    if (!followDate || isNaN(followDate.getTime())) return 3;

    // Reset time parts for accurate day comparison
    today.setHours(0, 0, 0, 0);
    followDate.setHours(0, 0, 0, 0);

    // Calculate difference in days
    const diffTime = followDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 1; // Overdue = highest priority
    if (diffDays <= 7) return 1; // Within 7 days = high priority
    if (diffDays <= 30) return 2; // Within 30 days = medium priority
    return 3; // More than 30 days = low priority
  }, [parseDate]);

  // Set initial data with default priority sorting
  useEffect(() => {
    if (filteredReports && Array.isArray(filteredReports)) {
      const sortedByPriority = [...filteredReports].sort((a, b) =>
        getColorPriority(a.FollowupDate) - getColorPriority(b.FollowupDate)
      );
      setLeadReports(sortedByPriority);
      setTableLeadReports(sortedByPriority);
    }
  }, [filteredReports, getColorPriority]);

  const [, setShowCategoryDropdown] = useState(false);
  const isL2User =
    String(userAuthLevel || "").trim().toUpperCase() === "L2";
  const [showTable, setShowTable] = useState(() =>
    isL2User ? readFollowupTableExpanded() : true
  );

  useEffect(() => {
    const l2 = String(userAuthLevel || "").trim().toUpperCase() === "L2";
    if (!l2) {
      setShowTable(true);
      return;
    }
    setShowTable(readFollowupTableExpanded());
  }, [userAuthLevel]);

  const handleToggleTable = () => {
    setShowTable((prev) => {
      const next = !prev;
      if (
        String(userAuthLevel || "").trim().toUpperCase() === "L2"
      ) {
        writeFollowupTableExpanded(next);
      }
      return next;
    });
  };

  const dropdownRefProbability = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefProbability.current &&
        !dropdownRefProbability.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    filterLeadReports(query);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      // If parsing fails, try parsing as MM-DD-YYYY format
      const parts = dateString.toString().split('-');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        const newDate = new Date(year, month - 1, day);
        if (!isNaN(newDate.getTime())) {
          const formattedMonth = String(newDate.getMonth() + 1).padStart(2, "0");
          const formattedDay = String(newDate.getDate()).padStart(2, "0");
          const formattedYear = newDate.getFullYear();
          return `${formattedMonth}-${formattedDay}-${formattedYear}`;
        }
      }
      return ""; // Return empty string for invalid dates
    }

    // Format the valid date as MM-DD-YYYY
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };



  const filterLeadReports = (query) => {
    const lowerCaseQuery = query.toLowerCase().trim();

    const filtered = LeadReports.filter((LeadReport) =>
      Object.entries(LeadReport).some(([key, value]) => {
        if (key === "comments" || key === "Reference" || key === "ClientContactName"
          || key === "TypeService" || key === "Probability" || key === "ClientPhone"
          || key === "ClientEmail" || key === "Source" || key === "LeadOwner" || key === "AssignedPeople"
          || key === "AssignedPeopleEmail" || key === "LeadOwnerEmail") return false;

        if (value !== null && value !== undefined) {
          if (key === "AssignedGroup" || key === "groupid") {
            return getLeadGroupName(LeadReport).toLowerCase().includes(lowerCaseQuery);
          }
          let formattedValue = value.toString().trim().toLowerCase();

          // Special handling for date fields
          if (key === "LeadDate" || key === "FollowupDate") {
            const formattedDate = formatDate(value);
            if (formattedDate) {
              formattedValue = formattedDate.toLowerCase();
            }
          }

          return formattedValue.includes(lowerCaseQuery);
        }
        return false;
      })
    );
    setTableLeadReports(filtered);
  };

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;



  const getRowClass = (followUpDate) => {
    const priority = getColorPriority(followUpDate);

    switch (priority) {
      case 1: return "bg-[#EE4B2B]"; // Red for high priority/overdue
      case 2: return "bg-orange-500"; // Orange for medium priority
      case 3: return "bg-green-500"; // Green for low priority
      default: return "bg-green-500";
    }
  };

  // Apply filtering and maintain priority sorting
  const pageItems = tableLeadReports.filter((LeadReport) => {
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

    const leadDate = parseDate(LeadReport.LeadDate);
    const status = LeadReport.Status?.toLowerCase() || "";

    return (
      leadDate >= twoYearsAgo &&
      (status === "pending" || status === "won recurring")
    );
  });

  const currentItems = pageItems.slice(startIdx, endIdx);

  const totalItems = pageItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const [, setUploadedFiles] = useState([]);
  const [, setFetchedFiles] = useState([])
  const [leadId,] = useState("")

  const fetchFiles = useCallback(async (leadId) => {
    if (!leadId) return;
    setUploadedFiles([]);
    setFetchedFiles([]);
    try {
      const response = await dispatch(fetchFilesByLeadId(leadId)).unwrap();
      setUploadedFiles(response);
      setFetchedFiles(response);
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  }, [dispatch, setUploadedFiles, setFetchedFiles]);

  useEffect(() => {
    if (!leadId) {
      setUploadedFiles([]);
      setFetchedFiles([]);
      return;
    }
    fetchFiles(leadId);
  }, [leadId, fetchFiles, setUploadedFiles]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [LeadToDelete, setLeadToDelete] = useState(null);

  const openDeleteModal = (id) => {
    setLeadToDelete(id);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setLeadToDelete(null);
  };

  const confirmDelete = async () => {
    if (LeadToDelete) {
      await deleteLeadReport(LeadToDelete);
    }
    closeDeleteModal();
  };

  const deleteLeadReport = async (id) => {
    try {
      const resultAction = await dispatch(deleteLead(id));

      if (deleteLead.fulfilled.match(resultAction)) {
        enqueueSnackbar("Lead report deleted successfully!", { variant: "success" });
        setFilteredLeadReports((prevReports) =>
          prevReports.filter((report) => report.id !== id)
        );
      } else {
        enqueueSnackbar("Failed to delete Lead report!", { variant: "error" });
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Connection Error!", { variant: "error" });
    }
  };

  // Modified sorting functions - sort the entire dataset before filtering
  const sortAlphabetically = (targetToSort, order) => {
    if (!Array.isArray(LeadReports) || LeadReports.length === 0) {
      console.warn("⚠️ filteredReports is not ready yet");
      return;
    }

    const key = `${targetToSort}-${order}`;
    let sorted = [];

    // Sort the entire filteredReports dataset, not just tableLeadReports
    const sourceArray = [...LeadReports];

    switch (key) {
      case "business-az":
        sorted = sourceArray.sort((a, b) =>
          getLeadGroupName(a).localeCompare(getLeadGroupName(b))
        );
        break;

      case "business-za":
        sorted = sourceArray.sort((a, b) =>
          getLeadGroupName(b).localeCompare(getLeadGroupName(a))
        );
        break;

      case "client-az":
        sorted = sourceArray.sort((a, b) =>
          a.ClientName.localeCompare(b.ClientName)
        );
        break;

      case "client-za":
        sorted = sourceArray.sort((a, b) =>
          b.ClientName.localeCompare(a.ClientName)
        );
        break;

      case "project-az":
        sorted = sourceArray.sort((a, b) =>
          a.ProjectName.localeCompare(b.ProjectName)
        );
        break;

      case "project-za":
        sorted = sourceArray.sort((a, b) =>
          b.ProjectName.localeCompare(a.ProjectName)
        );
        break;

      case "leadtype-az":
        sorted = sourceArray.sort((a, b) =>
          a.LeadType.localeCompare(b.LeadType)
        );
        break;

      case "leadtype-za":
        sorted = sourceArray.sort((a, b) =>
          b.LeadType.localeCompare(a.LeadType)
        );
        break;

      case "leadstatus-az":
        sorted = sourceArray.sort((a, b) =>
          a.Status.localeCompare(b.Status)
        );
        break;

      case "leadstatus-za":
        sorted = sourceArray.sort((a, b) =>
          b.Status.localeCompare(a.Status)
        );
        break;

      case "leadate-az":
        sorted = sourceArray.sort((a, b) => {
          const dateA = a.LeadDate ? new Date(formatDate(a.LeadDate)) : new Date(0);
          const dateB = b.LeadDate ? new Date(formatDate(b.LeadDate)) : new Date(0);
          return dateA - dateB;
        });
        break;

      case "leadate-za":
        sorted = sourceArray.sort((a, b) => {
          const dateA = a.LeadDate ? new Date(formatDate(a.LeadDate)) : new Date(0);
          const dateB = b.LeadDate ? new Date(formatDate(b.LeadDate)) : new Date(0);
          return dateB - dateA;
        });
        break;

      case "followupdate-az":
        sorted = sourceArray.sort((a, b) => {
          const dateA = a.FollowupDate ? new Date(formatDate(a.FollowupDate)) : new Date(0);
          const dateB = b.FollowupDate ? new Date(formatDate(b.FollowupDate)) : new Date(0);
          return dateA - dateB;
        });
        break;

      case "followupdate-za":
        sorted = sourceArray.sort((a, b) => {
          const dateA = a.FollowupDate ? new Date(formatDate(a.FollowupDate)) : new Date(0);
          const dateB = b.FollowupDate ? new Date(formatDate(b.FollowupDate)) : new Date(0);
          return dateB - dateA;
        });
        break;

      default:
        return;
    }

    // Update both the main state and table state
    setLeadReports(sorted);
    setTableLeadReports(sorted);
  };

  const numericSort = (order) => {
    if (!Array.isArray(LeadReports) || LeadReports.length === 0) {
      console.warn("⚠️ filteredReports is not ready yet");
      return;
    }
    const sorted = [...LeadReports].sort((a, b) =>
      order === "az"
        ? Number(a.QuotedValue) - Number(b.QuotedValue)
        : Number(b.QuotedValue) - Number(a.QuotedValue)
    );

    // Update both the main state and table state
    setLeadReports(sorted);
    setTableLeadReports(sorted);
  };

  useEffect(() => {
    dispatch(setFilteredReports());
  }, [dispatch]);

 


  useEffect(() => {
    if (!filteredReports?.length) return;

    const sortedByFollowupDate = [...filteredReports].sort((a, b) => {
      // Handle missing or invalid dates safely
      const dateA = a.FollowupDate ? new Date(a.FollowupDate) : new Date(0);
      const dateB = b.FollowupDate ? new Date(b.FollowupDate) : new Date(0);

      return dateA - dateB;
    });

    setFilteredLeadReports(sortedByFollowupDate);
    setLeadReports(sortedByFollowupDate);
    setTableLeadReports(sortedByFollowupDate);
  }, [filteredReports]);




  return (
    <div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
      />
      <div className="w-full bg-white rounded-md">
        <div className="flex justify-between p-2 items-center">
          {/* Left side */}
          <div className="flex w-1/2 justify-start items-start gap-2">
            <h1 className="text-2xl font-semibold capitalize">
              Follow-up Table
            </h1>
          </div>

          {/* Right side */}
          <div className="flex w-1/2 justify-end items-center gap-4">
            {showTable && (
              <div className=" flex items-center gap-2 p-1 bg-[#e0e7ff] rounded-full w-[320px] md:w-1/3">
                <MdSearch className="w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search"
                  className="bg-[#e0e7ff] outline-none w-full"
                />
              </div>
            )}

            <div
              onClick={handleToggleTable}
              className="flex justify-center items-center border rounded-md w-7 h-7 cursor-pointer"
            >
              <RemoveIcon className="w-7 h-7" />
            </div>
          </div>
        </div>

        {showTable && (
          <div>
            <div className="min-h-[25rem] ">
              <div className="head flex w-full bg-black text-white text-xs">
                <div className="w-[5%] flex justify-center align-middle items-center border border-white">Sr. No</div>
                <div className="w-[9.3%] p-1 flex justify-between align-middle items-center border border-white">Lead Date
                  <div className="flex flex-col gap-y-1">
                    <div
                      className="cursor-pointer "
                      onClick={() => sortAlphabetically("leadate", "az")}
                    >
                      <ArrowDropUp
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => sortAlphabetically("leadate", "za")}
                    >
                      <ArrowDropDown
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="w-[10.3%] p-1 flex justify-between align-middle items-center border border-white"> Client Name
                  <div className="flex flex-col gap-y-1">
                    <div
                      className="cursor-pointer "
                      onClick={() => sortAlphabetically("client", "az")}
                    >
                      <ArrowDropUp
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => sortAlphabetically("client", "za")}
                    >
                      <ArrowDropDown
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>
                  </div></div>
                <div className="w-[10.3%] p-1 flex justify-between align-middle items-center border border-white">Project Name
                  <div className="flex flex-col gap-y-1">
                    <div
                      className="cursor-pointer"
                      onClick={() => sortAlphabetically("project", "az")}
                    >
                      <ArrowDropUp
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => sortAlphabetically("project", "za")}
                    >
                      <ArrowDropDown
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>
                  </div></div>
                <div className="w-[9.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize">Lead Type
                  <div className="flex flex-col gap-y-1">
                    <div
                      className="cursor-pointer "
                      onClick={() => sortAlphabetically("leadtype", "az")}
                    >
                      <ArrowDropUp
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => sortAlphabetically("leadtype", "za")}
                    >
                      <ArrowDropDown
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>
                  </div></div>
                <div className="w-[10.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize"> Quoted Value
                  <div className="flex flex-col gap-y-1">
                    <div
                      className="cursor-pointer"
                      onClick={() => numericSort("za")}
                    >
                      <ArrowDropUp
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => numericSort("az")}
                    >
                      <ArrowDropDown
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>
                  </div></div>
                <div className="w-[9.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize">Status
                  <div className="flex flex-col gap-y-1">
                    <div
                      className="cursor-pointer "
                      onClick={() =>
                        sortAlphabetically("leadstatus", "az")
                      }
                    >
                      <ArrowDropUp
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() =>
                        sortAlphabetically("leadstatus", "za")
                      }
                    >
                      <ArrowDropDown
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>
                  </div></div>
                <div className="w-[9.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize">Group
                  <div className="flex flex-col gap-y-1">
                    <div
                      className="cursor-pointer"
                      onClick={() => sortAlphabetically("business", "az")}
                    >
                      <ArrowDropUp
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() => sortAlphabetically("business", "za")}
                    >
                      <ArrowDropDown
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>
                  </div></div>
                <div className="w-[10.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize"> Follow-up Date
                  <div className="flex flex-col gap-y-1">
                    <div
                      className="cursor-pointer"
                      onClick={() =>
                        sortAlphabetically("followupdate", "az")
                      }
                    >
                      <ArrowDropUp
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>

                    <div
                      className="cursor-pointer -mt-2"
                      onClick={() =>
                        sortAlphabetically("followupdate", "za")
                      }
                    >
                      <ArrowDropDown
                        color="white"
                        sx={{ fontSize: "22px" }}
                      />
                    </div>
                  </div></div>
                <div className="w-[9.3%] p-1 flex justify-center items-center border border-white break-words text-wrap whitespace-normal text-center text-capitalize">
                  Attachments
                </div>
                {userAuthLevel !== "L1" && (
                <div className="w-[5%] p-1 flex justify-center align-middle items-center border border-white">Edit
                </div>
                )}
                {userAuthLevel !== "L1" && (
                <div className="w-[5%] p-1 flex justify-center align-middle items-center border border-white"><FaTrash size={15} />
                </div>
                )}
              </div>
              <div>
                {currentItems.length > 0 ? (
                  currentItems.map((LeadReport, index) => (
                    <div key={LeadReport.id} className="body flex w-full border border-b-gray-400  text-xs text-capitalize">
                      <div className={`w-[5%] flex justify-center align-middle items-center ${getRowClass(LeadReport.FollowupDate)}`}>{index + startIdx + 1}</div>
                      <div className="w-[9.3%] p-1 flex justify-between align-middle items-center border border-white">
                        {LeadReport.LeadDate
                          ? formatDate(LeadReport.LeadDate)
                          : ""}
                      </div>
                      <div className="w-[10.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize capitalize">
                        {LeadReport.ClientName}
                      </div>
                      <div className="w-[10.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize capitalize">
                        {LeadReport.ProjectName}
                      </div>
                      <div className="w-[9.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize capitalize">
                        {LeadReport.LeadType}
                      </div>
                      <div className="w-[10.3%] p-1 flex justify-between align-middle items-center border border-white break-all whitespace-normal">
                        ${LeadReport.QuotedValue?.toString().replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ","
                        ) || "0"}
                      </div>
                      <div className="w-[9.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize capitalize" >
                        {LeadReport.Status}
                      </div>
                      <div className="w-[9.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize capitalize">
                        {getLeadGroupName(LeadReport)}
                      </div>
                      <div className="w-[10.3%] p-1 flex justify-between align-middle items-center border border-white text-capitalize">
                        {LeadReport.Status === "Won" ||
                          LeadReport.Status === "Lost"
                          ? ""
                          : formatDate(LeadReport.FollowupDate)}
                      </div>
                      <div className="w-[9.3%] relative p-1 flex gap-2 justify-center items-center border border-white break-words text-wrap whitespace-normal text-center">
                        <AttachFileIcon
                          className="rotate-12 cursor-pointer"
                          onClick={() => {
                            setShowModal(true);
                            setattchmentLead(LeadReport)
                          }}
                        />
                      </div>
                      {userAuthLevel !== "L1" && (
                      <div onClick={() => {
                            if (isViewOnly) return;
                            navigate(`/dashboard/edit-lead/${LeadReport.id}`);
                          }}
                        className={`w-[5%] p-1 flex justify-center align-middle items-center border border-white ${isViewOnly ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}>
                        <span title="Edit">
                          <MdEditSquare className="w-5 h-5" />
                        </span>
                      </div>
                      )}
                      {userAuthLevel !== "L1" && (
                      <div onClick={() => {
                            if (isViewOnly) return;
                            openDeleteModal(LeadReport.id);
                          }}
                        className={`w-[5%] p-1 flex justify-center align-middle items-center border border-white ${isViewOnly ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}>
                        <FaTrash size={15} />
                      </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div colSpan="9" className="p-4 text-red-500 text-center h-[20rem] font-semibold text-[18px]">
                    {searchQuery ? `No Result Found For "${searchQuery}"` : "No Result Found"}
                  </div>
                )}
              </div>
            </div>
            <div className="pagination mt-4 flex justify-between items-center bg-white rounded-md p-1">
              <div>
                {totalItems > 0 ? (
                  <>
                    Showing {startIdx + 1} to {Math.min(endIdx, totalItems)} of{" "}
                    {totalItems} Entries
                  </>
                ) : (
                  <>Showing 0 Entries</>
                )}
              </div>
              {totalPages > 1 && (
                <div>
                  <button
                    onClick={() => setCurrentPage(1)}
                    className="px-2 py-2 mx-1 border rounded"
                    disabled={currentPage === 1}
                    title="First Page"
                  >
                    <KeyboardDoubleArrowLeftIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    className="px-2 py-2 mx-2 border rounded"
                    disabled={currentPage === 1}
                  >
                    <ChevronRightIcon className="w-5 h-5 rotate-180" />
                  </button>

                  {Array.from({ length: 3 }, (_, index) => {
                    const pageNumber = currentPage - 1 + index;
                    if (pageNumber >= 1 && pageNumber <= totalPages) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-4 py-2 mx-2 border rounded ${currentPage === pageNumber ? "bg-gray-300" : ""
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="px-2 py-2 mx-2 border rounded"
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="px-2 py-2 mx-1 border rounded"
                    disabled={currentPage === totalPages}
                    title="Last Page"
                  >
                    <KeyboardDoubleArrowRightIcon className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {showModal && (
          <FileUploadFollowupTable LeadReport={attchmentLead} setShowModal={setShowModal} isViewOnly={isViewOnly} />
        )}
      </div>
    </div>
  );
};

export default FollowupTableL1;